import { useMemo } from "react";
import { marksFor, SERVICE_MARKS } from "../lib/services";
import { TAG_COLOR, theme } from "../lib/theme";
import type { Stop } from "../lib/types";

interface Props {
  /** Alle stopp i korridoren — tegnes som svak bakgrunn. */
  allStops: Stop[];
  /** Stoppene som faktisk inngår i ruten, i rekkefølge. */
  routeStops: Stop[];
  /** Stopp uthevet fordi det er valgt eller pekt på. */
  activeIds: string[];
  onSelect: (stop: Stop) => void;
  /** Meldes når pekeren går inn i eller ut av et stopp — driver bildekortet. */
  onHover?: (stop: Stop | null) => void;
}

/*
 * Størrelsen på merkene. Selve utvalget ligger i lib/services.ts, delt med
 * 3D-kartet og forklaringen under kartet.
 */
const MARK_SIZE = 13;
const MARK_GAP = 2;

const WIDTH = 1000;
const HEIGHT = 380;
const PADDING = { top: 34, right: 34, bottom: 34, left: 34 };

/** Omtrentlig tekstbredde i px — SVG kan ikke måle før det er tegnet. */
const CHAR_WIDTH = 6.3;
const LABEL_HEIGHT = 15;

interface Placed {
  x: number;
  y: number;
  anchor: "start" | "end" | "middle";
  box: { left: number; right: number; top: number; bottom: number };
}

/**
 * Kart over korridoren, tegnet direkte fra stoppenes ekte koordinater med
 * en enkel Mercator-projeksjon. Ingen kartbibliotek og ingen fliser å laste —
 * geometrien i datasettet er nok til å gjøre ruten gjenkjennelig.
 *
 * Etikettene plasseres med en grådig kollisjonstest: hvert navn prøver fire
 * posisjoner rundt punktet sitt og tar den første som er ledig. Uten det
 * legger navn som Hallingskarvet og Hallingskeid seg oppå hverandre, siden
 * stedene ligger få kilometer fra hverandre.
 */
export function RouteMap({ allStops, routeStops, activeIds, onSelect, onHover }: Props) {
  const project = useMemo(() => {
    const mercatorY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));

    const lons = allStops.map((s) => s.lon);
    const ys = allStops.map((s) => mercatorY(s.lat));
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const innerW = WIDTH - PADDING.left - PADDING.right;
    const innerH = HEIGHT - PADDING.top - PADDING.bottom;

    return (stop: Stop) => ({
      x: PADDING.left + ((stop.lon - minLon) / (maxLon - minLon || 1)) * innerW,
      // Mercator-y vokser nordover, SVG-y vokser nedover.
      y: PADDING.top + (1 - (mercatorY(stop.lat) - minY) / (maxY - minY || 1)) * innerH,
    });
  }, [allStops]);

  const corridor = useMemo(
    () => [...allStops].sort((a, b) => a.position - b.position),
    [allStops],
  );

  const routePath = useMemo(() => {
    if (routeStops.length < 2) return "";
    const points = routeStops.map(project);

    // Myk, men behersket kurve. Kontrollpunktene ligger på 25 % av avstanden,
    // ikke på nabopunktets høyde — ellers svinger streken langt forbi stoppene
    // og ruten leser som en berg-og-dalbane framfor en reise.
    return points
      .map((point, index) => {
        if (index === 0) return `M ${point.x.toFixed(1)} ${point.y.toFixed(1)}`;
        const previous = points[index - 1];
        const dx = (point.x - previous.x) * 0.25;
        return (
          `C ${(previous.x + dx).toFixed(1)} ${previous.y.toFixed(1)}, ` +
          `${(point.x - dx).toFixed(1)} ${point.y.toFixed(1)}, ` +
          `${point.x.toFixed(1)} ${point.y.toFixed(1)}`
        );
      })
      .join(" ");
  }, [routeStops, project]);

  /**
   * Plasserer etikettene så de ikke overlapper. Kandidatposisjonene prøves i
   * rekkefølge: høyre, venstre, over, under.
   */
  const labels = useMemo(() => {
    const placed: Placed[] = [];
    const result = new Map<string, Placed>();

    /*
     * Merkene tegnes først, så etikettene må vike for dem. Uten dette la
     * navnet på et stopp seg rett oppå stoppets egne tjenestemerker — den
     * fjerde kandidatposisjonen er nettopp «under punktet».
     */
    for (const stop of routeStops) {
      const count = marksFor(stop).length;
      if (count === 0) continue;
      const point = project(stop);
      const width = count * MARK_SIZE + (count - 1) * MARK_GAP;
      placed.push({
        x: point.x,
        y: point.y,
        anchor: "middle",
        box: {
          left: point.x - width / 2,
          right: point.x + width / 2,
          top: point.y + 9,
          bottom: point.y + 11 + MARK_SIZE,
        },
      });
    }

    for (const stop of routeStops) {
      const point = project(stop);
      const width = stop.name.length * CHAR_WIDTH;

      const candidates: Placed[] = [
        { x: point.x + 12, y: point.y + 4, anchor: "start" },
        { x: point.x - 12, y: point.y + 4, anchor: "end" },
        { x: point.x, y: point.y - 14, anchor: "middle" },
        { x: point.x, y: point.y + 22, anchor: "middle" },
      ].map((c) => ({
        ...c,
        anchor: c.anchor as Placed["anchor"],
        box: {
          left: c.anchor === "start" ? c.x : c.anchor === "end" ? c.x - width : c.x - width / 2,
          right: c.anchor === "start" ? c.x + width : c.anchor === "end" ? c.x : c.x + width / 2,
          top: c.y - LABEL_HEIGHT + 4,
          bottom: c.y + 4,
        },
      }));

      const fits = (candidate: Placed) =>
        candidate.box.left >= 2 &&
        candidate.box.right <= WIDTH - 2 &&
        !placed.some(
          (other) =>
            candidate.box.left < other.box.right + 3 &&
            other.box.left < candidate.box.right + 3 &&
            candidate.box.top < other.box.bottom + 2 &&
            other.box.top < candidate.box.bottom + 2,
        );

      // Ingen ledig plass: bruk første kandidat likevel, heller enn å skjule navnet.
      const chosen = candidates.find(fits) ?? candidates[0];
      placed.push(chosen);
      result.set(stop.id, chosen);
    }

    return result;
  }, [routeStops, project]);

  const routeIds = new Set(routeStops.map((s) => s.id));
  const active = new Set(activeIds);

  return (
    <svg
      className="map-svg"
      // Se StopHoverCard: kortet lukker seg når pekeren forlater alle kilder.
      data-hover-source=""
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={`${routeStops.at(0)?.name ?? ""} – ${routeStops.at(-1)?.name ?? ""}`}
    >
      <defs>
        <linearGradient id="routeGradient" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#8F3D17" />
          <stop offset="100%" stopColor={theme.color.accent} />
        </linearGradient>
      </defs>

      {/* Hele korridoren som svak referanse bak ruten. */}
      <path
        d={corridor
          .map((stop, index) => {
            const point = project(stop);
            return `${index === 0 ? "M" : "L"} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`;
          })
          .join(" ")}
        fill="none"
        stroke={theme.color.sand}
        strokeWidth={1.5}
        strokeDasharray="2 6"
        strokeOpacity={0.7}
      />

      {/* Den valgte ruten: bred glød under, tydelig strek over. */}
      {routePath && (
        <>
          <path
            d={routePath}
            fill="none"
            stroke="url(#routeGradient)"
            strokeWidth={11}
            strokeOpacity={0.13}
            strokeLinecap="round"
          />
          <path
            d={routePath}
            fill="none"
            stroke="url(#routeGradient)"
            strokeWidth={3}
            strokeLinecap="round"
          />
        </>
      )}

      {/* Stopp som ikke er med i ruten */}
      {corridor
        .filter((stop) => !routeIds.has(stop.id))
        .map((stop) => {
          const point = project(stop);
          return (
            <g
              key={stop.id}
              className="map-stop"
              onClick={() => onSelect(stop)}
              onMouseEnter={() => onHover?.(stop)}
              onMouseLeave={() => onHover?.(null)}
            >
              <circle cx={point.x} cy={point.y} r={10} fill="transparent" />
              <circle cx={point.x} cy={point.y} r={2.5} fill={theme.color.mist} opacity={0.45} />
              <title>{stop.name}</title>
            </g>
          );
        })}

      {/* Stopp i ruten */}
      {routeStops.map((stop, index) => {
        const point = project(stop);
        const isActive = active.has(stop.id);
        const isEndpoint = index === 0 || index === routeStops.length - 1;
        const color = TAG_COLOR[stop.tags[0]] ?? theme.color.accent;
        const label = labels.get(stop.id)!;

        return (
          <g
            key={stop.id}
            className="map-stop"
            onClick={() => onSelect(stop)}
            onMouseEnter={() => onHover?.(stop)}
            onMouseLeave={() => onHover?.(null)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(stop);
              }
            }}
          >
            <circle cx={point.x} cy={point.y} r={14} fill="transparent" />

            {/* Ring rundt endepunktene, så start og mål skiller seg ut. */}
            {isEndpoint && (
              <circle
                cx={point.x}
                cy={point.y}
                r={10}
                fill="none"
                stroke={theme.color.fjord}
                strokeWidth={1.5}
                strokeOpacity={0.35}
              />
            )}

            <circle
              cx={point.x}
              cy={point.y}
              r={isActive ? 8 : isEndpoint ? 6.5 : 5}
              fill={isEndpoint ? theme.color.fjord : color}
              stroke={theme.color.birch}
              strokeWidth={isActive ? 3 : 2}
            />

            {/* Hvit kontur bak teksten, så navn kan krysse rutelinja og fortsatt leses. */}
            <text
              x={label.x}
              y={label.y}
              textAnchor={label.anchor}
              fontFamily={theme.font.mono}
              fontSize={11}
              fontWeight={isActive || isEndpoint ? 700 : 500}
              stroke={theme.color.birch}
              strokeWidth={3.5}
              strokeLinejoin="round"
              opacity={0.9}
            >
              {stop.name}
            </text>
            <text
              x={label.x}
              y={label.y}
              textAnchor={label.anchor}
              fontFamily={theme.font.mono}
              fontSize={11}
              fontWeight={isActive || isEndpoint ? 700 : 500}
              fill={isActive ? theme.color.accent : theme.color.fjord}
            >
              {stop.name}
            </text>

            {/* Tjenestemerker under punktet. Se SERVICE_MARKS. */}
            <ServiceMarks stop={stop} x={point.x} y={point.y + (isActive ? 13 : 11)} />

            <title>{`${stop.name} — ${stop.elevationM} m`}</title>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Én rad med små merker under et stoppunkt, ett per tjeneste stedet har.
 *
 * Tegnet som grupper med `transform` framfor nøstede `<svg x y>`: begge er
 * gyldig SVG, men transformen holder ikonene på plass når kartet skaleres i
 * viewBox-en, og den er det samme uttrykket som resten av kartet bruker.
 */
function ServiceMarks({ stop, x, y }: { stop: Stop; x: number; y: number }) {
  const marks = marksFor(stop);
  if (marks.length === 0) return null;

  const total = marks.length * MARK_SIZE + (marks.length - 1) * MARK_GAP;
  const left = x - total / 2;

  return (
    <g className="map-marks" aria-hidden="true">
      {marks.map((mark, index) => {
        const Icon = mark.icon;
        const markX = left + index * (MARK_SIZE + MARK_GAP);
        return (
          <g key={mark.key} transform={`translate(${markX.toFixed(1)} ${y.toFixed(1)})`}>
            <rect
              width={MARK_SIZE}
              height={MARK_SIZE}
              rx={3.5}
              fill={theme.color.birch}
              stroke={mark.color}
              strokeWidth={1}
              strokeOpacity={0.55}
            />
            <g transform={`translate(${(MARK_SIZE - 9) / 2} ${(MARK_SIZE - 9) / 2})`}>
              <Icon size={9} color={mark.color} strokeWidth={2.4} />
            </g>
          </g>
        );
      })}
    </g>
  );
}
